import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import {
  getCustomers,
  getCustomersPaginated,
  createCustomer,
  updateCustomer,
  deleteCustomer,
  createOrRetryCustomerDriveFolder,
  getNextCustomerNumber,
  getCustomerNumberingSettings,
  updateCustomerNumberingSettings,
  bulkDeleteCustomers,
  bulkImportCustomers,
  sendWelcomeEmail,
  bootstrapCustomerMappingReview,
  getCustomerMappingReviewSummary,
  getCustomerMappingReviewItems,
  searchCustomerMappingReference,
  assignCustomerMappingReview,
  skipCustomerMappingReview,
  getCustomerNoSyncSummary,
  bulkSyncCustomerNumbersFromSheet,
  backfillCustomerNumbersFromSheet,
} from '@/lib/api';
import {
  Plus, Search, Edit2, Trash2, Users, Upload, Download, FileText,
  MoreHorizontal, Mail, CheckCircle2, Loader2, Store, ChevronLeft, ChevronRight,
  RefreshCw, Link2, ArrowUpDown, ArrowUp, ArrowDown, Scissors, FolderOpen,
} from 'lucide-react';

// Store config: store_id → { label, senderEmail }
const STORE_CONFIG = {
  store_singapore: { label: 'Singapore', senderEmail: 'admin@perfectattire.com' },
  store_perth:     { label: 'Perth',     senderEmail: 'perth@perfectattire.com' },
  store_internal:  { label: 'Internal',  senderEmail: 'admin@perfectattire.com' },
};

const getStoreSender = (store) => {
  if (!store) return 'admin@perfectattire.com';
  return STORE_CONFIG[store]?.senderEmail || 'admin@perfectattire.com';
};

const getStoreLabel = (store) => {
  if (!store) return '—';
  return STORE_CONFIG[store]?.label || store;
};

// ---- Welcome Email Confirmation Dialog ----
function WelcomeEmailDialog({ customer, open, onClose, onSent }) {
  const [sending, setSending] = useState(false);
  if (!customer) return null;

  const fromEmail = getStoreSender(customer.store);
  const alreadySent = !!customer.welcome_email_sent_at;

  const handleSend = async () => {
    setSending(true);
    try {
      await sendWelcomeEmail({
        customer_name: customer.customer_name,
        customer_email: customer.email,
        customer_id: customer.customer_id,
        store: customer.store || 'singapore',
      });
      toast.success(`Welcome email sent to ${customer.email}`);
      onSent(customer.customer_id);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-blue-600" />
            Send Welcome Email
          </DialogTitle>
          <DialogDescription>
            Review the details below before sending.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          {alreadySent && (
            <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
              <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" />
              A welcome email was already sent on {new Date(customer.welcome_email_sent_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}. Sending again will resend it.
            </div>
          )}

          <div className="rounded-lg border border-slate-200 divide-y divide-slate-100 text-sm">
            <div className="flex items-start gap-3 px-4 py-3">
              <span className="text-slate-400 w-14 flex-shrink-0 pt-0.5 text-xs uppercase tracking-wide font-medium">To</span>
              <div>
                <p className="font-semibold text-slate-800">{customer.customer_name}</p>
                <p className="text-slate-500">{customer.email}</p>
              </div>
            </div>
            <div className="flex items-start gap-3 px-4 py-3">
              <span className="text-slate-400 w-14 flex-shrink-0 pt-0.5 text-xs uppercase tracking-wide font-medium">From</span>
              <div>
                <p className="font-medium text-slate-700">{fromEmail}</p>
                <p className="text-slate-400 text-xs">{getStoreLabel(customer.store)} store</p>
              </div>
            </div>
            <div className="flex items-start gap-3 px-4 py-3">
              <span className="text-slate-400 w-14 flex-shrink-0 pt-0.5 text-xs uppercase tracking-wide font-medium">Subject</span>
              <p className="text-slate-600">Welcome to Perfect Attire!</p>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={sending}>Cancel</Button>
          <Button onClick={handleSend} disabled={sending} className="bg-blue-600 hover:bg-blue-700 text-white">
            {sending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending…</> : <><Mail className="w-4 h-4 mr-2" />Send Email</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---- Main Page ----
const CustomersPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(100);
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [sortBy, setSortBy] = useState('customer_no');
  const [sortOrder, setSortOrder] = useState('desc');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [formData, setFormData] = useState({
    customer_no: '',
    customer_name: '',
    email: '',
    phone_number: '',
    notes: '',
    store: '',
  });

  // Bulk selection
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  // Welcome email state
  const [emailDialogCustomer, setEmailDialogCustomer] = useState(null);
  const [bulkEmailConfirmOpen, setBulkEmailConfirmOpen] = useState(false);
  const [bulkSending, setBulkSending] = useState(false);

  // Drive folder automation state
  const [creatingDriveFolder, setCreatingDriveFolder] = useState({});

  // CSV import
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [csvData, setCsvData] = useState([]);
  const [importPreview, setImportPreview] = useState([]);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  // Customer number mapping review
  const [activeTab, setActiveTab] = useState('all-clients');
  const [reviewItems, setReviewItems] = useState([]);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewSummary, setReviewSummary] = useState(null);
  const [reviewSearch, setReviewSearch] = useState('');
  const [reviewPage, setReviewPage] = useState(1);
  const [reviewTotal, setReviewTotal] = useState(0);
  const [reviewTotalPages, setReviewTotalPages] = useState(1);
  const [reviewStatusFilter, setReviewStatusFilter] = useState('open');
  const [reviewIssueTypeFilter, setReviewIssueTypeFilter] = useState('all');
  const [mappingExcelUrl, setMappingExcelUrl] = useState(() => localStorage.getItem('customer-mapping-sheet-url') || '');
  const [bootstrappingReview, setBootstrappingReview] = useState(false);
  const [assigningReviewId, setAssigningReviewId] = useState('');
  const [rowAssignments, setRowAssignments] = useState({});
  const [rowCandidates, setRowCandidates] = useState({});

  const canManageCustomerNumbering = ['owner', 'admin'].includes(user?.role);
  const [autoGenerateCustomerNo, setAutoGenerateCustomerNo] = useState(false);
  const [numberingLoading, setNumberingLoading] = useState(false);
  const [numberingSaving, setNumberingSaving] = useState(false);
  const [customerNoSyncSummary, setCustomerNoSyncSummary] = useState(null);
  const [customerNoSyncLoading, setCustomerNoSyncLoading] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim());
    }, 300);

    return () => window.clearTimeout(timer);
  }, [searchTerm]);

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getCustomersPaginated({
        page: currentPage,
        page_size: pageSize,
        search: debouncedSearchTerm || undefined,
        sort_by: sortBy,
        sort_order: sortOrder,
      });

      const payload = response?.data || {};
      setCustomers(payload.data || []);
      setTotalCustomers(payload.total || 0);
      setTotalPages(payload.total_pages || 1);
    } catch {
      toast.error('Failed to fetch customers');
      setCustomers([]);
      setTotalCustomers(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, debouncedSearchTerm, sortBy, sortOrder]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  useEffect(() => {
    setCurrentPage(1);
    setSelectedCustomers([]);
  }, [debouncedSearchTerm, sortBy, sortOrder]);

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortBy(field);
    setSortOrder(field === 'customer_no' ? 'desc' : 'asc');
  };

  const renderSortIcon = (field) => {
    if (sortBy !== field) return <ArrowUpDown className="w-3.5 h-3.5" />;
    return sortOrder === 'asc' ? <ArrowUp className="w-3.5 h-3.5" /> : <ArrowDown className="w-3.5 h-3.5" />;
  };

  const fetchReviewSummary = useCallback(async () => {
    try {
      const response = await getCustomerMappingReviewSummary();
      setReviewSummary(response?.data || null);
      const sourceUrl = response?.data?.reference_source_sheet_url;
      const isGoogleSheet = /docs\.google\.com\/spreadsheets/i.test(String(sourceUrl || ''));
      if (sourceUrl && isGoogleSheet && !mappingExcelUrl) {
        setMappingExcelUrl(sourceUrl);
      }
    } catch (error) {
      if (activeTab === 'unmatched-review') {
        toast.error(error.response?.data?.detail || 'Failed to load unmatched review summary');
      }
    }
  }, [activeTab, mappingExcelUrl]);

  const fetchReviewItems = useCallback(async () => {
    setReviewLoading(true);
    try {
      const response = await getCustomerMappingReviewItems({
        status: reviewStatusFilter,
        issue_type: reviewIssueTypeFilter,
        search: reviewSearch || undefined,
        page: reviewPage,
        page_size: 50,
      });
      const payload = response?.data || {};
      const items = payload.data || [];
      setReviewItems(items);
      setReviewTotal(payload.total || 0);
      setReviewTotalPages(payload.total_pages || 1);

      const candidateSeed = {};
      const assignmentSeed = {};
      items.forEach((item) => {
        candidateSeed[item.review_id] = item.candidates || [];
        assignmentSeed[item.review_id] = item.current_customer_no ? String(item.current_customer_no) : '';
      });
      setRowCandidates(candidateSeed);
      setRowAssignments(assignmentSeed);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load unmatched review items');
      setReviewItems([]);
      setReviewTotal(0);
      setReviewTotalPages(1);
    } finally {
      setReviewLoading(false);
    }
  }, [reviewStatusFilter, reviewIssueTypeFilter, reviewSearch, reviewPage]);

  useEffect(() => {
    if (activeTab !== 'unmatched-review') return;
    fetchReviewSummary();
    fetchReviewItems();
  }, [activeTab, fetchReviewSummary, fetchReviewItems]);

  const fetchCustomerNumberingSettings = useCallback(async () => {
    if (!canManageCustomerNumbering) return;
    setNumberingLoading(true);
    try {
      const response = await getCustomerNumberingSettings();
      setAutoGenerateCustomerNo(Boolean(response?.data?.auto_generate_customer_no));
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to load customer numbering settings');
    } finally {
      setNumberingLoading(false);
    }
  }, [canManageCustomerNumbering]);

  const fetchCustomerNoSyncSummary = useCallback(async () => {
    setCustomerNoSyncLoading(true);
    try {
      const response = await getCustomerNoSyncSummary();
      setCustomerNoSyncSummary(response?.data || null);
    } catch (error) {
      console.warn('Failed to fetch customer number sync summary', error);
    } finally {
      setCustomerNoSyncLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCustomerNumberingSettings();
  }, [fetchCustomerNumberingSettings]);

  useEffect(() => {
    fetchCustomerNoSyncSummary();
  }, [fetchCustomerNoSyncSummary]);

  useEffect(() => {
    setReviewPage(1);
  }, [reviewSearch, reviewStatusFilter, reviewIssueTypeFilter]);

  const handleBootstrapReview = async () => {
    if (!mappingExcelUrl.trim()) {
      toast.error('Please enter the Google Sheet URL first');
      return;
    }
    setBootstrappingReview(true);
    try {
      await bootstrapCustomerMappingReview({ sheet_url: mappingExcelUrl.trim() });
      localStorage.setItem('customer-mapping-sheet-url', mappingExcelUrl.trim());
      toast.success('Unmatched review queue rebuilt from Google Sheet');
      setReviewPage(1);
      await fetchReviewSummary();
      await fetchReviewItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to build unmatched review queue');
    } finally {
      setBootstrappingReview(false);
    }
  };

  const handleRefreshCandidates = async (item) => {
    const fallbackQuery = item.email || item.phone_number || item.customer_name;
    if (!fallbackQuery) {
      toast.error('No query data found for this client');
      return;
    }
    try {
      const response = await searchCustomerMappingReference(fallbackQuery, 25);
      const candidates = response?.data?.data || [];
      setRowCandidates((prev) => ({ ...prev, [item.review_id]: candidates }));
      toast.success(`Loaded ${candidates.length} candidate(s)`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load suggestions');
    }
  };

  const handleAssignReview = async (item) => {
    const raw = (rowAssignments[item.review_id] || '').trim();
    const parsed = Number.parseInt(raw, 10);
    if (!raw || Number.isNaN(parsed) || parsed <= 0) {
      toast.error('Enter a valid customer number');
      return;
    }

    setAssigningReviewId(item.review_id);
    try {
      await assignCustomerMappingReview(item.review_id, {
        customer_no: parsed,
        source_label: 'unmatched-review-manual',
      });
      toast.success(`Assigned customer number ${parsed}`);
      fetchCustomers();
      await fetchReviewSummary();
      await fetchReviewItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign customer number');
    } finally {
      setAssigningReviewId('');
    }
  };

  const handleSkipReview = async (item) => {
    setAssigningReviewId(item.review_id);
    try {
      await skipCustomerMappingReview(item.review_id, {
        note: 'Skipped from unmatched review screen',
      });
      toast.success('Marked as skipped');
      await fetchReviewSummary();
      await fetchReviewItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to skip review item');
    } finally {
      setAssigningReviewId('');
    }
  };

  const fetchNextCustomerNumber = async () => {
    if (!autoGenerateCustomerNo) return null;
    try {
      const response = await getNextCustomerNumber();
      return response.data.next_customer_no;
    } catch {
      return 1;
    }
  };

  const handleToggleAutoCustomerNumbering = async (checked) => {
    if (!canManageCustomerNumbering) return;
    setNumberingSaving(true);
    try {
      await updateCustomerNumberingSettings({ auto_generate_customer_no: checked });
      setAutoGenerateCustomerNo(checked);
      toast.success(checked ? 'Auto customer numbering enabled' : 'Auto customer numbering disabled');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to update customer numbering settings');
    } finally {
      setNumberingSaving(false);
    }
  };

  const handleOpenNewCustomer = async () => {
    resetForm();
    if (autoGenerateCustomerNo) {
      const nextNo = await fetchNextCustomerNumber();
      setFormData(prev => ({ ...prev, customer_no: nextNo || '' }));
    } else {
      setFormData(prev => ({ ...prev, customer_no: '' }));
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        customer_no: formData.customer_no ? parseInt(formData.customer_no) : null,
        store: formData.store || null,
      };
      if (editingCustomer) {
        await updateCustomer(editingCustomer.customer_id, submitData);
        toast.success('Customer updated');
      } else {
        await createCustomer(submitData);
        toast.success('Customer created');
      }
      setDialogOpen(false);
      resetForm();
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleEdit = (customer) => {
    setEditingCustomer(customer);
    setFormData({
      customer_no: customer.customer_no || '',
      customer_name: customer.customer_name,
      email: customer.email || '',
      phone_number: customer.phone_number || '',
      notes: customer.notes || '',
      store: customer.store || '',
    });
    setDialogOpen(true);
  };

  const handleDelete = async (customerId) => {
    if (!window.confirm('Are you sure you want to delete this customer?')) return;
    try {
      await deleteCustomer(customerId);
      toast.success('Customer deleted');
      fetchCustomers();
    } catch {
      toast.error('Failed to delete customer');
    }
  };

  const resetForm = () => {
    setEditingCustomer(null);
    setFormData({ customer_no: '', customer_name: '', email: '', phone_number: '', notes: '', store: '' });
  };

  // Bulk selection
  const handleSelectAll = (checked) => {
    setSelectedCustomers(checked ? customers.map(c => c.customer_id) : []);
  };

  const handleSelectCustomer = (customerId, checked) => {
    setSelectedCustomers(prev => checked ? [...prev, customerId] : prev.filter(id => id !== customerId));
  };

  const handleBulkDelete = async () => {
    try {
      await bulkDeleteCustomers(selectedCustomers);
      toast.success(`Deleted ${selectedCustomers.length} customer(s)`);
      setSelectedCustomers([]);
      setDeleteConfirmOpen(false);
      fetchCustomers();
    } catch {
      toast.error('Failed to delete customers');
    }
  };

  // Welcome email sent callback — update local state immediately
  const handleEmailSent = (customerId) => {
    const now = new Date().toISOString();
    setCustomers(prev => prev.map(c =>
      c.customer_id === customerId ? { ...c, welcome_email_sent_at: now } : c
    ));
  };

  // Drive folder creation/retry
  const handleCreateDriveFolder = async (customer) => {
    setCreatingDriveFolder(prev => ({ ...prev, [customer.customer_id]: true }));
    try {
      const response = await createOrRetryCustomerDriveFolder(customer.customer_id);
      const url = response?.data?.client_folder_url;
      setCustomers(prev => prev.map(c =>
        c.customer_id === customer.customer_id
          ? {
              ...c,
              drive_folder_status: response?.data?.dry_run ? 'simulated' : 'created',
              drive_folder_url: url,
              drive_folder_error: null,
            }
          : c
      ));
      toast.success(response?.data?.dry_run ? 'Drive folder simulated (no Google folder created)' : 'Drive folder created');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create Drive folder');
    } finally {
      setCreatingDriveFolder(prev => ({ ...prev, [customer.customer_id]: false }));
    }
  };

  // Bulk welcome email
  const selectedWithEmail = selectedCustomers
    .map(id => customers.find(c => c.customer_id === id))
    .filter(c => c && c.email);

  const handleBulkSendEmail = async () => {
    setBulkSending(true);
    let sent = 0;
    let failed = 0;
    for (const c of selectedWithEmail) {
      try {
        await sendWelcomeEmail({
          customer_name: c.customer_name,
          customer_email: c.email,
          customer_id: c.customer_id,
          store: c.store || 'singapore',
        });
        handleEmailSent(c.customer_id);
        sent++;
      } catch {
        failed++;
      }
    }
    setBulkSending(false);
    setBulkEmailConfirmOpen(false);
    if (sent > 0) toast.success(`Welcome email sent to ${sent} client(s)${failed ? `, ${failed} failed` : ''}`);
    if (failed > 0 && sent === 0) toast.error(`Failed to send ${failed} email(s)`);
  };

  // CSV
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      if (typeof event.target?.result === 'string') parseCSV(event.target.result);
    };
    reader.readAsText(file);
  };

  const parseCSV = (text) => {
    const lines = text.split('\n').filter(line => line.trim());
    if (lines.length < 2) { toast.error('CSV file is empty or has no data rows'); return; }
    const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/"/g, ''));
    const headerMap = {
      customer_no: ['customer_no','customer no','no','number','id','customer number','cust_no'],
      customer_name: ['customer_name','name','customer name','full name','fullname'],
      email: ['email','e-mail','email address'],
      phone_number: ['phone_number','phone','phone number','telephone','mobile','cell'],
      notes: ['notes','note','comments','comment','remarks'],
    };
    const findIdx = (field) => {
      for (const v of (headerMap[field] || [field])) {
        const i = headers.indexOf(v); if (i !== -1) return i;
      }
      return -1;
    };
    const idx = {
      customer_no: findIdx('customer_no'), customer_name: findIdx('customer_name'),
      email: findIdx('email'), phone_number: findIdx('phone_number'), notes: findIdx('notes'),
    };
    if (idx.customer_name === -1) { toast.error('CSV must have a "name" or "customer_name" column'); return; }
    const parsedData = [];
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].match(/(".*?"|[^",]+)(?=\s*,|\s*$)/g)?.map(v => v.replace(/^"|"$/g, '').trim()) || [];
      if (!values.length) continue;
      const row = {
        customer_no: idx.customer_no !== -1 ? parseInt(values[idx.customer_no]) || null : null,
        customer_name: idx.customer_name !== -1 ? values[idx.customer_name] : '',
        email: idx.email !== -1 ? values[idx.email] : '',
        phone_number: idx.phone_number !== -1 ? values[idx.phone_number] : '',
        notes: idx.notes !== -1 ? values[idx.notes] : '',
      };
      if (row.customer_name) parsedData.push(row);
    }
    if (!parsedData.length) { toast.error('No valid customer data found in CSV'); return; }
    setCsvData(parsedData);
    setImportPreview(parsedData.slice(0, 5));
    setImportDialogOpen(true);
  };

  const handleImport = async () => {
    if (!csvData.length) return;
    setImporting(true);
    try {
      const response = await bulkImportCustomers(csvData);
      toast.success(response.data.message);
      setImportDialogOpen(false);
      setCsvData([]); setImportPreview([]);
      if (fileInputRef.current) fileInputRef.current.value = '';
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import customers');
    } finally {
      setImporting(false);
    }
  };

  const handleBulkSyncCustomerNos = async () => {
    if (!selectedCustomers.length) {
      toast.error('Select at least one customer');
      return;
    }
    try {
      const response = await bulkSyncCustomerNumbersFromSheet(selectedCustomers);
      const data = response?.data || {};
      toast.success(`Synced ${data.success_count || 0}, pending ${data.pending_count || 0}`);
      fetchCustomers();
      fetchCustomerNoSyncSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync selected customers');
    }
  };

  const handleBackfillCustomerNos = async () => {
    try {
      const response = await backfillCustomerNumbersFromSheet({ limit: 300, only_with_orders: true });
      const data = response?.data || {};
      toast.success(`Backfill processed ${data.selected || 0}: synced ${data.success_count || 0}, pending ${data.pending_count || 0}`);
      fetchCustomers();
      fetchCustomerNoSyncSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Backfill sync failed');
    }
  };

  const downloadSampleCSV = () => {
    const sample = `customer_no,customer_name,email,phone_number,notes\n1,John Doe,john.doe@example.com,+1234567890,VIP Customer\n2,Jane Smith,jane.smith@example.com,+0987654321,Regular customer`;
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([sample], { type: 'text/csv' }));
    a.download = 'customers_sample.csv';
    a.click();
  };

  return (
    <div className="space-y-8 p-3" data-testid="customers-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">Customers</h1>
          <p className="text-muted-foreground text-sm" data-testid="customers-total-summary">Manage your client database ({totalCustomers} total)</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <input ref={fileInputRef} type="file" accept=".csv" onChange={handleFileSelect} className="hidden" id="csv-upload" />
            <Button variant="outline" onClick={() => fileInputRef.current?.click()} className="flex items-center gap-2">
              <Upload className="w-4 h-4" />Import CSV
            </Button>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) resetForm(); setDialogOpen(open); }}>
            <DialogTrigger asChild>
              <Button className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="add-customer-button" onClick={handleOpenNewCustomer}>
                <Plus className="w-4 h-4 mr-2" />Add Customer
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="text-lg font-semibold">{editingCustomer ? 'Edit Customer' : 'New Customer'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Customer No.</Label>
                    <Input
                      type="number"
                      value={formData.customer_no}
                      onChange={(e) => setFormData({ ...formData, customer_no: e.target.value })}
                      placeholder={autoGenerateCustomerNo ? 'Auto-generated' : 'Optional (auto-generation off)'}
                      className="h-9"
                      data-testid="customer-number-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Name *</Label>
                    <Input data-testid="customer-name-input" value={formData.customer_name} onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })} placeholder="Enter name" className="h-11 bg-secondary/50" required />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="label-uppercase">Email</Label>
                  <Input data-testid="customer-email-input" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="Enter email address" className="h-11 bg-secondary/50" />
                </div>
                <div className="space-y-2">
                  <Label className="label-uppercase">Phone</Label>
                  <Input data-testid="customer-phone-input" value={formData.phone_number} onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })} placeholder="Enter phone number" className="h-11 bg-secondary/50" />
                </div>
                <div className="space-y-2">
                  <Label className="label-uppercase">Store <span className="text-muted-foreground font-normal normal-case">(optional)</span></Label>
                  <Select value={formData.store || 'none'} onValueChange={(v) => setFormData({ ...formData, store: v === 'none' ? '' : v })}>
                    <SelectTrigger className="h-11 bg-secondary/50">
                      <SelectValue placeholder="Select store…" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— No store —</SelectItem>
                      {Object.entries(STORE_CONFIG).map(([id, cfg]) => (
                        <SelectItem key={id} value={id}>{cfg.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="label-uppercase">Notes</Label>
                  <Textarea data-testid="customer-notes-input" value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="Additional notes..." className="bg-secondary/50 min-h-[80px]" />
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" data-testid="save-customer-button" className="bg-primary text-primary-foreground hover:bg-primary/90">
                    {editingCustomer ? 'Update' : 'Create'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-5" data-testid="customers-tabs-container">
        <TabsList data-testid="customers-tabs-list">
          <TabsTrigger value="all-clients" data-testid="customers-tab-all-clients">All Clients</TabsTrigger>
          <TabsTrigger value="unmatched-review" data-testid="customers-tab-unmatched-review">Unmatched Review</TabsTrigger>
        </TabsList>

        <TabsContent value="all-clients" className="space-y-5" data-testid="customers-tab-content-all-clients">
          {canManageCustomerNumbering && (
            <Card className="border-border bg-card shadow-sm rounded-xl" data-testid="customer-numbering-settings-card">
              <CardContent className="p-4 md:p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="space-y-1">
                    <p className="text-sm font-semibold text-foreground" data-testid="customer-numbering-settings-title">Customer Numbering</p>
                    <p className="text-xs text-muted-foreground" data-testid="customer-numbering-settings-description">
                      Auto-generate customer numbers for new registrations. Turn this off while Singapore import is pending.
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge variant={autoGenerateCustomerNo ? 'default' : 'outline'} data-testid="customer-numbering-settings-status">
                      {autoGenerateCustomerNo ? 'ON' : 'OFF'}
                    </Badge>
                    <Switch
                      checked={autoGenerateCustomerNo}
                      onCheckedChange={handleToggleAutoCustomerNumbering}
                      disabled={numberingLoading || numberingSaving}
                      data-testid="customer-numbering-settings-switch"
                    />
                  </div>
                </div>
                {!autoGenerateCustomerNo && (
                  <p className="mt-3 text-xs text-amber-700" data-testid="customer-numbering-settings-off-note">
                    Auto generation is currently OFF. New customers can be created with no customer number.
                  </p>
                )}

                <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="customer-number-sync-actions-row">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleBackfillCustomerNos}
                    disabled={customerNoSyncLoading}
                    data-testid="customer-number-sync-backfill-button"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Backfill from Google Sheet
                  </Button>
                  <Badge variant="outline" data-testid="customer-number-sync-summary-badge">
                    Pending: {customerNoSyncSummary?.pending_customers ?? '—'}
                  </Badge>
                  <Badge variant="outline" data-testid="customer-number-sync-missing-badge">
                    Missing No: {customerNoSyncSummary?.missing_customer_no ?? '—'}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Search and Bulk Actions */}
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="relative max-w-md flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input data-testid="customer-search-input" placeholder="Search by name, email, phone or customer no..." value={searchTerm} onChange={(e) => {
                setSearchTerm(e.target.value);
              }} className="pl-10 h-11 bg-secondary/50" />
            </div>
            {selectedCustomers.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="secondary" className="h-8 px-3">{selectedCustomers.length} selected</Badge>
                {selectedWithEmail.length > 0 && (
                  <Button variant="outline" size="sm" onClick={() => setBulkEmailConfirmOpen(true)} className="flex items-center gap-2 text-blue-700 border-blue-300 hover:bg-blue-50">
                    <Mail className="w-4 h-4" />Send Welcome Email ({selectedWithEmail.length})
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleBulkSyncCustomerNos}
                  className="flex items-center gap-2 text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                  data-testid="customers-bulk-sync-customer-no-button"
                >
                  <Link2 className="w-4 h-4" />Sync Cust No (Sheet)
                </Button>
                <Button variant="destructive" size="sm" onClick={() => setDeleteConfirmOpen(true)} className="flex items-center gap-2">
                  <Trash2 className="w-4 h-4" />Delete ({selectedCustomers.length})
                </Button>
              </div>
            )}
          </div>

          {/* Table */}
          <Card className="border-border bg-card shadow-sm rounded-xl">
            <CardContent className="p-0">
              {loading ? (
                <div className="p-6 space-y-4">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}</div>
              ) : customers.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <Users className="w-12 h-12 text-muted-foreground mb-4" strokeWidth={1} />
                  <h3 className="text-lg font-medium mb-1">No customers found</h3>
                  <p className="text-muted-foreground text-sm">{searchTerm ? 'Try a different search term' : 'Add your first customer to get started'}</p>
                </div>
              ) : (
                <Table className="table-luxury">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">
                        <Checkbox
                          checked={selectedCustomers.length === customers.length && customers.length > 0}
                          onCheckedChange={handleSelectAll}
                          data-testid="customers-select-all-checkbox"
                        />
                      </TableHead>
                      <TableHead className="w-28">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-auto px-0 py-0 font-medium hover:bg-transparent"
                          onClick={() => toggleSort('customer_no')}
                          data-testid="customers-sort-customer-no-button"
                        >
                          <span className="mr-1">Customer No</span>
                          {renderSortIcon('customer_no')}
                        </Button>
                      </TableHead>
                      <TableHead>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-auto px-0 py-0 font-medium hover:bg-transparent"
                          onClick={() => toggleSort('customer_name')}
                          data-testid="customers-sort-customer-name-button"
                        >
                          <span className="mr-1">Name</span>
                          {renderSortIcon('customer_name')}
                        </Button>
                      </TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Store</TableHead>
                      <TableHead>Notes</TableHead>
                      <TableHead className="w-12 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {customers.map((customer) => (
                      <TableRow key={customer.customer_id} className="table-row-hover">
                        <TableCell>
                          <Checkbox
                            checked={selectedCustomers.includes(customer.customer_id)}
                            onCheckedChange={(checked) => handleSelectCustomer(customer.customer_id, checked)}
                            data-testid={`customers-select-${customer.customer_id}-checkbox`}
                          />
                        </TableCell>
                        <TableCell className="font-mono text-primary font-medium">{customer.customer_no || '—'}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{customer.customer_name}</span>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="h-6 px-2 text-[11px]"
                              onClick={() => window.open(`/fitting-studio/${customer.customer_id}`, '_blank', 'noopener,noreferrer')}
                              data-testid={`customers-open-fitting-studio-${customer.customer_id}`}
                            >
                              <Scissors className="w-3 h-3 mr-1" />
                              Fitting Studio
                            </Button>
                            {customer.welcome_email_sent_at && (
                              <span title={`Welcome email sent ${new Date(customer.welcome_email_sent_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`} className="flex items-center gap-1 text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-1.5 py-0.5 font-medium">
                                <CheckCircle2 className="w-3 h-3" />Email sent
                              </span>
                            )}
                            {customer.drive_folder_status === 'simulated' ? (
                              <span
                                title="Drive folder path ran in dry-run; no Google folder was created"
                                className="flex items-center gap-1 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-1.5 py-0.5 font-medium"
                                data-testid={`customers-drive-folder-simulated-${customer.customer_id}`}
                              >
                                <FolderOpen className="w-3 h-3" />Drive (sim)
                              </span>
                            ) : customer.drive_folder_status === 'created' && customer.drive_folder_url ? (
                              <a
                                href={customer.drive_folder_url}
                                target="_blank"
                                rel="noreferrer"
                                title="Open Drive folder"
                                className="flex items-center gap-1 text-[11px] text-blue-700 bg-blue-50 border border-blue-200 rounded-full px-1.5 py-0.5 font-medium hover:bg-blue-100"
                                data-testid={`customers-drive-folder-link-${customer.customer_id}`}
                              >
                                <FolderOpen className="w-3 h-3" />Drive
                              </a>
                            ) : customer.customer_no ? (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="h-6 px-2 text-[11px]"
                                onClick={() => handleCreateDriveFolder(customer)}
                                disabled={creatingDriveFolder[customer.customer_id]}
                                title={customer.drive_folder_status === 'failed' ? customer.drive_folder_error : undefined}
                                data-testid={`customers-create-drive-folder-${customer.customer_id}`}
                              >
                                {creatingDriveFolder[customer.customer_id] ? (
                                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                ) : (
                                  <FolderOpen className="w-3 h-3 mr-1" />
                                )}
                                {customer.drive_folder_status === 'failed' ? 'Retry Drive' : 'Drive Folder'}
                              </Button>
                            ) : null}
                          </div>
                        </TableCell>
                        <TableCell className="text-muted-foreground">{customer.email || '—'}</TableCell>
                        <TableCell className="text-muted-foreground">{customer.phone_number || '—'}</TableCell>
                        <TableCell>
                          {customer.store ? (
                            <Badge variant="outline" className="text-[11px] font-medium text-slate-600 border-slate-300">
                              {getStoreLabel(customer.store)}
                            </Badge>
                          ) : <span className="text-muted-foreground">—</span>}
                        </TableCell>
                        <TableCell className="text-muted-foreground max-w-[160px] truncate">{customer.notes || '—'}</TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8 hover:bg-slate-100">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                              <DropdownMenuItem
                                onClick={() => setEmailDialogCustomer(customer)}
                                disabled={!customer.email}
                                className="flex items-center gap-2 cursor-pointer"
                              >
                                <Mail className="w-4 h-4 text-blue-600" />
                                <span>Send Welcome Email</span>
                                {customer.welcome_email_sent_at && <CheckCircle2 className="w-3 h-3 text-emerald-500 ml-auto" />}
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => handleEdit(customer)} className="flex items-center gap-2 cursor-pointer">
                                <Edit2 className="w-4 h-4" /><span>Edit</span>
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleDelete(customer.customer_id)} className="flex items-center gap-2 cursor-pointer text-destructive focus:text-destructive">
                                <Trash2 className="w-4 h-4" /><span>Delete</span>
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}

              {!loading && totalPages > 1 && (
                <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-4 py-3" data-testid="customers-pagination-container">
                  <div className="text-sm text-muted-foreground" data-testid="customers-pagination-summary">
                    Page {currentPage} of {totalPages}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                      disabled={currentPage <= 1}
                      data-testid="customers-pagination-prev-button"
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" />
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                      disabled={currentPage >= totalPages}
                      data-testid="customers-pagination-next-button"
                    >
                      Next
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="unmatched-review" className="space-y-5" data-testid="customers-tab-content-unmatched-review">
          <Card className="border-border bg-card shadow-sm rounded-xl">
            <CardContent className="p-4 md:p-5 space-y-4">
              <div className="grid grid-cols-1 xl:grid-cols-3 gap-3 items-end">
                <div className="xl:col-span-2 space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Google Sheet URL</Label>
                  <Input
                    value={mappingExcelUrl}
                    onChange={(e) => setMappingExcelUrl(e.target.value)}
                    placeholder="Paste Google Sheet link"
                    data-testid="unmatched-review-sheet-url-input"
                  />
                </div>
                <Button
                  onClick={handleBootstrapReview}
                  disabled={bootstrappingReview}
                  className="w-full"
                  data-testid="unmatched-review-rebuild-button"
                >
                  {bootstrappingReview ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Building…</> : <><RefreshCw className="w-4 h-4 mr-2" />Rebuild Review Queue</>}
                </Button>
              </div>

              <div className="flex flex-wrap items-center gap-2" data-testid="unmatched-review-summary-row">
                <Badge variant="secondary" data-testid="unmatched-review-open-total">Open: {reviewSummary?.summary?.open?.total ?? 0}</Badge>
                <Badge variant="outline" data-testid="unmatched-review-open-unmatched">Unmatched: {reviewSummary?.summary?.open?.unmatched ?? 0}</Badge>
                <Badge variant="outline" data-testid="unmatched-review-open-conflicts">Conflicts: {reviewSummary?.summary?.open?.conflict ?? 0}</Badge>
                <Badge variant="outline" data-testid="unmatched-review-resolved-total">Resolved: {reviewSummary?.summary?.resolved?.total ?? 0}</Badge>
              </div>

              <div className="flex flex-wrap gap-2 items-center">
                <Input
                  value={reviewSearch}
                  onChange={(e) => setReviewSearch(e.target.value)}
                  placeholder="Search unmatched clients by name/email/phone"
                  className="max-w-md"
                  data-testid="unmatched-review-search-input"
                />
                <Select value={reviewStatusFilter} onValueChange={setReviewStatusFilter}>
                  <SelectTrigger className="w-[170px]" data-testid="unmatched-review-status-filter">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                    <SelectItem value="skipped">Skipped</SelectItem>
                    <SelectItem value="all">All</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={reviewIssueTypeFilter} onValueChange={setReviewIssueTypeFilter}>
                  <SelectTrigger className="w-[170px]" data-testid="unmatched-review-issue-filter">
                    <SelectValue placeholder="Issue type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Issues</SelectItem>
                    <SelectItem value="unmatched">Unmatched</SelectItem>
                    <SelectItem value="conflict">Conflict</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          <Card className="border-border bg-card shadow-sm rounded-xl">
            <CardContent className="p-0">
              {reviewLoading ? (
                <div className="p-6 space-y-4">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-20 w-full" />)}</div>
              ) : reviewItems.length === 0 ? (
                <div className="py-14 text-center" data-testid="unmatched-review-empty-state">
                  <p className="text-sm text-muted-foreground">No unmatched review items for current filters.</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Client</TableHead>
                      <TableHead>Issue</TableHead>
                      <TableHead>Current No.</TableHead>
                      <TableHead>Suggested Matches</TableHead>
                      <TableHead>Assign Number</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reviewItems.map((item) => {
                      const candidates = rowCandidates[item.review_id] || [];
                      return (
                        <TableRow key={item.review_id} data-testid={`unmatched-review-row-${item.review_id}`}>
                          <TableCell>
                            <div className="space-y-1">
                              <div className="font-medium" data-testid={`unmatched-review-name-${item.review_id}`}>{item.customer_name}</div>
                              <div className="text-xs text-muted-foreground" data-testid={`unmatched-review-email-${item.review_id}`}>{item.email || '—'}</div>
                              <div className="text-xs text-muted-foreground" data-testid={`unmatched-review-phone-${item.review_id}`}>{item.phone_number || '—'}</div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant={item.issue_type === 'conflict' ? 'destructive' : 'outline'} data-testid={`unmatched-review-issue-${item.review_id}`}>
                              {item.issue_type}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-mono" data-testid={`unmatched-review-current-no-${item.review_id}`}>{item.current_customer_no ?? '—'}</TableCell>
                          <TableCell>
                            <div className="space-y-2 min-w-[220px]">
                              <Select
                                value={rowAssignments[item.review_id] || 'none'}
                                onValueChange={(value) => {
                                  if (value !== 'none') {
                                    setRowAssignments((prev) => ({ ...prev, [item.review_id]: value }));
                                  }
                                }}
                              >
                                <SelectTrigger data-testid={`unmatched-review-candidate-select-${item.review_id}`}>
                                  <SelectValue placeholder="Select suggested match" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">No suggestion selected</SelectItem>
                                  {candidates.map((candidate) => (
                                    <SelectItem
                                      key={`${item.review_id}-${candidate.customer_no}`}
                                      value={String(candidate.customer_no)}
                                    >
                                      #{candidate.customer_no} · {candidate.full_name || 'Unknown'}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleRefreshCandidates(item)}
                                data-testid={`unmatched-review-refresh-candidates-${item.review_id}`}
                              >
                                <Link2 className="w-4 h-4 mr-1" />Refresh suggestions
                              </Button>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Input
                              value={rowAssignments[item.review_id] || ''}
                              onChange={(event) => setRowAssignments((prev) => ({ ...prev, [item.review_id]: event.target.value }))}
                              placeholder="Customer no"
                              className="w-[130px]"
                              data-testid={`unmatched-review-assign-input-${item.review_id}`}
                            />
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button
                                size="sm"
                                onClick={() => handleAssignReview(item)}
                                disabled={assigningReviewId === item.review_id}
                                data-testid={`unmatched-review-assign-button-${item.review_id}`}
                              >
                                {assigningReviewId === item.review_id ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Assign'}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleSkipReview(item)}
                                disabled={assigningReviewId === item.review_id}
                                data-testid={`unmatched-review-skip-button-${item.review_id}`}
                              >
                                Skip
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}

              {!reviewLoading && reviewTotalPages > 1 && (
                <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-4 py-3" data-testid="unmatched-review-pagination-container">
                  <div className="text-sm text-muted-foreground" data-testid="unmatched-review-pagination-summary">
                    Page {reviewPage} of {reviewTotalPages} ({reviewTotal} items)
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setReviewPage((prev) => Math.max(1, prev - 1))}
                      disabled={reviewPage <= 1}
                      data-testid="unmatched-review-pagination-prev"
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" />Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setReviewPage((prev) => Math.min(reviewTotalPages, prev + 1))}
                      disabled={reviewPage >= reviewTotalPages}
                      data-testid="unmatched-review-pagination-next"
                    >
                      Next<ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Welcome Email Confirmation Dialog */}
      <WelcomeEmailDialog
        customer={emailDialogCustomer}
        open={!!emailDialogCustomer}
        onClose={() => setEmailDialogCustomer(null)}
        onSent={handleEmailSent}
      />

      {/* Bulk Email Confirm */}
      <AlertDialog open={bulkEmailConfirmOpen} onOpenChange={setBulkEmailConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><Mail className="w-5 h-5 text-blue-600" />Send Welcome Emails?</AlertDialogTitle>
            <AlertDialogDescription>
              This will send a welcome email to {selectedWithEmail.length} client(s). Each email will be sent from the store-specific sender address.
              {selectedWithEmail.length > 0 && (
                <span className="block mt-2 text-xs">
                  {selectedWithEmail.slice(0, 3).map(c => `${c.customer_name} (${c.email})`).join(', ')}
                  {selectedWithEmail.length > 3 && ` and ${selectedWithEmail.length - 3} more`}
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkSending}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleBulkSendEmail} disabled={bulkSending} className="bg-blue-600 hover:bg-blue-700">
              {bulkSending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending…</> : 'Send All'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Confirmation */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectedCustomers.length} Customer(s)?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone. This will permanently delete the selected customers from your database.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleBulkDelete} className="bg-destructive hover:bg-destructive/90">Delete All</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* CSV Import Preview */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileText className="w-5 h-5" />Import Customers from CSV</DialogTitle>
            <DialogDescription>Review the data before importing. {csvData.length} customer(s) found.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="border rounded-md overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-16">No.</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Phone</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {importPreview.map((row, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="font-mono">{row.customer_no || 'Auto'}</TableCell>
                      <TableCell>{row.customer_name}</TableCell>
                      <TableCell className="text-muted-foreground">{row.email || '—'}</TableCell>
                      <TableCell className="text-muted-foreground">{row.phone_number || '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {csvData.length > 5 && <p className="text-sm text-muted-foreground text-center">... and {csvData.length - 5} more customer(s)</p>}
            <div className="flex items-center justify-between p-3 bg-secondary/30 rounded-md">
              <span className="text-sm text-muted-foreground">Need a template?</span>
              <Button variant="link" size="sm" onClick={downloadSampleCSV} className="text-primary"><Download className="w-4 h-4 mr-1" />Download Sample CSV</Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setImportDialogOpen(false); setCsvData([]); setImportPreview([]); }}>Cancel</Button>
            <Button onClick={handleImport} disabled={importing}>{importing ? 'Importing...' : `Import ${csvData.length} Customer(s)`}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomersPage;
